import { Grid } from '@bedrock-layout/primitives'
import { ArticleCard, Img, PageTitle, Heading, Button } from '@okam/brand-ui'
import { safeTruncate, strippedString } from '@okam/core-lib'
import { useHtml2React } from '@okam/html2react/provider'
import { useTranslation } from 'next-i18next'
import { css } from 'twin.macro'
import { MainLayout } from 'components/layout'
import type { TDirectusProps } from 'components/layout/MainLayout'
import Seo from 'components/Seo'
import type { SeoContentFragment } from 'gql/graphql'
import { QueryKeys, useArticles, useArticlesArchivePage } from 'lib/react-query'

export interface TArticlesArchivePage {
  locale: string
  seo: { seo: SeoContentFragment | null | undefined; pageTitle: string }
  directusProps: TDirectusProps
}

const ArticlesArchivePage = ({ locale, seo, directusProps }: TArticlesArchivePage) => {
  const { data: pageData, isSuccess: pageSuccess } = useArticlesArchivePage(QueryKeys.GetArticlesArchivePage, locale)
  const { data: articlesData, isSuccess: articlesSuccess } = useArticles(QueryKeys.GetArticles, locale)

  const backgroundColor = pageData?.colorScheme?.background

  const { t } = useTranslation('common')

  const imageIndex = (articlesData?.articles?.length ?? 4) - ((articlesData?.articles?.length ?? 4) % 4)
  const firstPartofArticles = articlesData?.articles?.slice(0, imageIndex)
  const secondPartofArticles = articlesData?.articles?.slice(imageIndex)

  const pageTitle = pageData?.translations?.[0]?.title
  const { parseHTMLToReact } = useHtml2React()

  const parentUrlSlug = pageData?.parent?.translations?.[0]?.slug
  const parentTitle = strippedString(pageData?.parent?.translations?.[0]?.title ?? '')

  return (
    <>
      <Seo directusData={directusProps} seo={seo?.seo} pageTitle={seo?.pageTitle} />
      <MainLayout
        footerProps={directusProps}
        logoColor={pageData?.colorScheme?.accent}
        tw="bg-[theme('colors.primary[4]')]"
      >
        {pageSuccess && (
          <div
            css={css`
              background-color: ${backgroundColor};
            `}
            tw="pt-40"
          >
            {parentUrlSlug && (
              <Button
                icon="ArrowLeft"
                iconPosition="left"
                as="a"
                buttonStyle="hollow"
                href={`/${locale}/${parentUrlSlug}`}
                tw="bg-transparent w-fit mx-auto"
                css={[{ color: pageData?.colorScheme?.accent }]}
              >
                <Heading tw="text-center" variant="h5" as="p">
                  {t('ROUTES.GOBACK', { p: parentTitle })}
                </Heading>
              </Button>
            )}
            {pageTitle && (
              <PageTitle
                css={css`
                  background-color: ${backgroundColor};
                  padding-top: 1.5rem;
                `}
              >
                {parseHTMLToReact(pageTitle)}
              </PageTitle>
            )}
            <div tw="hidden lg:block absolute right-0 top-28 z-20">
              <Img src="/shared-assets/images/chat-7.png" alt="chat" width={300} height={300} />
            </div>
            <Grid
              gutter="1rem"
              minItemWidth={300}
              css={css`
                grid-template-columns: repeat(auto-fit, minmax(max(18rem, 100%/5), 1fr)) !important;
                position: relative;
                z-index: 30;
              `}
            >
              {articlesSuccess &&
                firstPartofArticles?.map((article) => {
                  const translation = article?.translations?.[0]

                  const image = article?.translations?.[0]?.featuredImage
                    ? article?.translations?.[0]?.featuredImage
                    : article?.featuredImage

                  return (
                    <ArticleCard
                      key={article.id}
                      bgColor=""
                      link={t('URL.SINGLE_ARTICLE', { slug: translation?.slug ?? '' })}
                      text={translation?.content ?? ''}
                      title={safeTruncate(translation?.title ?? '', 100)}
                      excerpt={translation?.excerpt}
                    >
                      <Img
                        css={css`
                          aspect-ratio: 1/1;
                          object-fit: cover;
                        `}
                        id={image?.id}
                        filename={image?.filenameDownload}
                        alt={image?.description ?? ''}
                        width={image?.width ?? 500}
                        height={image?.height ?? 500}
                      />
                    </ArticleCard>
                  )
                })}
              <Img src="/shared-assets/images/chat-8.png" alt="chat" width={300} height={300} />
              {articlesSuccess &&
                secondPartofArticles?.map((article) => {
                  const image = article?.translations?.[0]?.featuredImage
                    ? article?.translations?.[0]?.featuredImage
                    : article?.featuredImage

                  const translation = article?.translations?.[0]
                  return (
                    <ArticleCard
                      key={article.id}
                      bgColor=""
                      link={t('URL.SINGLE_ARTICLE', { slug: translation?.slug ?? '' })}
                      text={translation?.content ?? ''}
                      title={safeTruncate(translation?.title ?? '', 100)}
                      excerpt={translation?.excerpt}
                    >
                      <Img
                        css={css`
                          aspect-ratio: 1/1;
                          object-fit: cover;
                        `}
                        id={image?.id}
                        filename={image?.filenameDownload}
                        alt={image?.description ?? ''}
                        width={image?.width ?? 500}
                        height={image?.height ?? 500}
                      />
                    </ArticleCard>
                  )
                })}
            </Grid>
          </div>
        )}
      </MainLayout>
    </>
  )
}

export default ArticlesArchivePage
